import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtDeadline = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });

const outcome = (pv) => {
  if (!pv.paid) return "No payment has been taken, so your booking will be cancelled straight away and nothing is due.";
  if (pv.refund_amount > 0) return `You are cancelling more than 48 hours before arrival, so you will get a full refund of ${inr(pv.refund_amount)} to your UPI account.`;
  return "Your stay begins in less than 48 hours, so under our cancellation policy the full amount is charged and no refund is due.";
};

export const CancelRequested = ({ req }) => (
  <section className="bg-white border border-[#E2DCD0] p-6 sm:p-8" data-testid="cancel-requested">
    <p className="eyebrow text-[#C28B5E]">Cancellation</p>
    <h2 className="mt-1 text-2xl text-[#1E3B2E]">Cancellation requested</h2>
    <p className="mt-3 text-sm text-[#3b3b3b]">We received your request on {new Date(req.requested_at).toLocaleString("en-IN")}. Our team will confirm it shortly.</p>
    <p className="mt-2 text-sm">Refund due: <strong data-testid="cancel-requested-refund">{inr(req.refund_amount)}</strong></p>
  </section>
);

export default function CancelPanel({ booking, token, onUpdated }) {
  const pv = booking.cancel_preview;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/cancel`, { token, reason });
      toast.success(pv.paid ? "Cancellation request sent" : "Booking cancelled");
      onUpdated(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="no-print bg-white border border-[#E2DCD0] p-6 sm:p-8" data-testid="cancel-panel">
      <p className="eyebrow text-[#C28B5E]">Cancellation</p>
      <h2 className="mt-1 text-2xl text-[#1E3B2E]">Need to cancel?</h2>
      <p className="mt-3 text-sm text-[#3b3b3b]">{pv.free_window ? <>Free cancellation until <strong>{fmtDeadline(pv.deadline)}</strong> (48 hours before arrival).</> : <>Free cancellation ended on <strong>{fmtDeadline(pv.deadline)}</strong> (48 hours before arrival).</>} After that, the full amount is charged.</p>
      <div className="mt-4 border border-[#E2DCD0] bg-[#FDFBF7] p-4 text-sm" data-testid="cancel-refund-preview">
        <div className="flex justify-between gap-4"><span className="text-[#6b6b6b]">Refund if you cancel now</span><strong className="font-['Cormorant_Garamond'] text-2xl text-[#1E3B2E]" data-testid="cancel-refund-amount">{inr(pv.refund_amount)}</strong></div>
        <p className="mt-2 text-xs text-[#6b6b6b]">{outcome(pv)}</p>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="btn-ghost mt-5 text-red-700 hover:bg-red-700 hover:text-white" data-testid="cancel-booking-btn">{pv.paid ? "Request cancellation" : "Cancel booking"}</button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Cormorant_Garamond'] text-3xl text-[#1E3B2E]">Cancel booking {booking.booking_id}?</AlertDialogTitle>
            <AlertDialogDescription>{outcome(pv)}</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea className="field !h-24 py-3" maxLength={500} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="cancel-dialog-keep-btn">Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={busy} className="rounded-none bg-red-700 hover:bg-red-800" data-testid="cancel-dialog-confirm-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
